import {t, useLanguage, interfaceLocale} from '../../i18n';
import {useRegionMoney,useBusinessRegion,assertSameCurrency} from '../../utils/businessRegionContext';
import React, { useEffect, useMemo, useState } from "react";
import fsClient from "../../data/firestoreClient";
import { db as defaultDb, auth as defaultAuth } from "../../firebase";
import "./ExpenseLedger.css";

const EXPENSE_SCHEMA_VERSION = 1;
const PAGE_SIZE = 20;

const EXPENSE_CATEGORIES = [
  { key: "rent", label: "Alquiler" },
  { key: "payroll_fixed", label: "Nómina fija" },
  { key: "utilities", label: "Servicios" },
  { key: "software", label: "Software" },
  { key: "marketing", label: "Marketing" },
  { key: "cleaning", label: "Limpieza" },
  { key: "maintenance", label: "Mantenimiento" },
  { key: "general_supplies", label: "Insumos generales (no inventario)" },
  { key: "bank_fees", label: "Comisiones bancarias" },
  { key: "taxes", label: "Impuestos / tasas" },
  { key: "professional_services", label: "Servicios profesionales" },
  { key: "other", label: "Otros" },
];

const PAYMENT_METHODS = [
  { key: "cash", label: "Efectivo" },
  { key: "card", label: "Tarjeta" },
  { key: "transfer", label: "Transferencia" },
  { key: "ach", label: "ACH" },
  { key: "yappy", label: "Yappy" },
  { key: "check", label: "Cheque" },
  { key: "other", label: "Otro" },
];

const RECURRENCE_OPTIONS = [
  { key: "none", label: "No recurrente" },
  { key: "weekly", label: "Semanal" },
  { key: "monthly", label: "Mensual" },
  { key: "yearly", label: "Anual" },
];

const CATEGORY_LABELS = { ...Object.fromEntries(EXPENSE_CATEGORIES.map((item) => [item.key, item.label])), loop_fees: "Comisiones de Loop" };
const METHOD_LABELS = { ...Object.fromEntries(PAYMENT_METHODS.map((item) => [item.key, item.label])), withheld: "Retenida del cobro" };

const MONEY = new Intl.NumberFormat("es-PA", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function localDateKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromKey(key) {
  const match = String(key || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isKeyInRange(key, start, end) {
  const date = dateFromKey(key);
  if (!date) return false;
  if (start instanceof Date && Number.isFinite(start.getTime()) && date < start) return false;
  if (end instanceof Date && Number.isFinite(end.getTime()) && date >= end) return false;
  return true;
}

function normalizeExpense(row = {}) {
  const id = String(row?.id || row?.__documentId || "").trim();
  const amountCents = Math.max(0, Math.round(Number(row?.amountCents) || 0));
  const active = row?.active !== false;
  const status = String(row?.status || "paid").toLowerCase() === "pending" ? "pending" : "paid";
  return {
    ...row,
    id,
    schemaVersion: Number(row?.schemaVersion || EXPENSE_SCHEMA_VERSION),
    amountCents,
    amount: amountCents / 100,
    currency: String(row?.currency || "USD"),
    dateKey: String(row?.dateKey || ""),
    paidDateKey: String(row?.paidDateKey || ""),
    category: String(row?.category || "other"),
    vendor: String(row?.vendor || ""),
    paymentMethod: String(row?.paymentMethod || "other"),
    expenseType: String(row?.expenseType || "variable") === "fixed" ? "fixed" : "variable",
    recurrence: ["none", "weekly", "monthly", "yearly"].includes(String(row?.recurrence || "none")) ? String(row.recurrence || "none") : "none",
    status,
    notes: String(row?.notes || ""),
    active,
    voidedAt: String(row?.voidedAt || ""),
    voidedByUid: String(row?.voidedByUid || ""),
  };
}

function emptyForm() {
  const today = localDateKey();
  return {
    id: "",
    dateKey: today,
    amount: "",
    category: "rent",
    vendor: "",
    paymentMethod: "transfer",
    expenseType: "fixed",
    recurrence: "none",
    status: "paid",
    paidDateKey: today,
    notes: "",
  };
}

function centsFromInput(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  const amount = Number(normalized);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
}

function formatDateKey(key) {
  const date = dateFromKey(key);
  if (!date) return "Sin fecha";
  return date.toLocaleDateString(interfaceLocale(), { day: "numeric", month: "short", year: "numeric" }).replace(".", "");
}

export default function ExpenseLedger({
  businessId,
  db = defaultDb,
  auth = defaultAuth,
  open = false,
  onClose,
  currentStart = null,
  currentEnd = null,
  previousStart = null,
  previousEnd = null,
  periodLabel = "Período",
  onSummaryChange,
}) {
 useLanguage();
  const region=useBusinessRegion();
  const MONEY=useRegionMoney();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(() => emptyForm());
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [scope, setScope] = useState("period");
  const [recordFilter, setRecordFilter] = useState("active");
  const [page, setPage] = useState(1);

  const collectionPath = businessId ? `businessFinance/${businessId}/expenses` : "";

  const loadExpenses = async () => {
    if (!businessId || !collectionPath) {
      setExpenses([]);
      return;
    }
    setLoading(true);
    setStatusMessage("");
    try {
      const rows = await fsClient.listCollectionByPath(collectionPath, {}, { db });
      setExpenses(assertSameCurrency(Array.isArray(rows) ? rows : [], region).map(normalizeExpense));
    } catch (error) {
      console.error("[ExpenseLedger] load failed", error);
      setStatusMessage("No pudimos cargar los gastos. Si esta función acaba de activarse, revisa las reglas de Firestore.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const activeExpenses = useMemo(() => expenses.filter((row) => row.active !== false), [expenses]);
  const voidedExpenses = useMemo(() => expenses.filter((row) => row.active === false), [expenses]);

  const incurredRows = useMemo(
    () => activeExpenses.filter((row) => isKeyInRange(row.dateKey, currentStart, currentEnd)),
    [activeExpenses, currentStart, currentEnd]
  );

  const paidRows = useMemo(
    () => activeExpenses.filter((row) => row.status === "paid" && isKeyInRange(row.paidDateKey || row.dateKey, currentStart, currentEnd)),
    [activeExpenses, currentStart, currentEnd]
  );

  const previousIncurredRows = useMemo(
    () => activeExpenses.filter((row) => isKeyInRange(row.dateKey, previousStart, previousEnd)),
    [activeExpenses, previousStart, previousEnd]
  );

  const previousPaidRows = useMemo(
    () => activeExpenses.filter((row) => row.status === "paid" && isKeyInRange(row.paidDateKey || row.dateKey, previousStart, previousEnd)),
    [activeExpenses, previousStart, previousEnd]
  );

  const summary = useMemo(() => {
    const incurredCents = incurredRows.reduce((sum, row) => sum + row.amountCents, 0);
    const paidCents = paidRows.reduce((sum, row) => sum + row.amountCents, 0);
    const pendingCents = incurredRows.filter((row) => row.status === "pending").reduce((sum, row) => sum + row.amountCents, 0);
    const previousIncurredCents = previousIncurredRows.reduce((sum, row) => sum + row.amountCents, 0);
    const previousPaidCents = previousPaidRows.reduce((sum, row) => sum + row.amountCents, 0);
    const byCategoryMap = new Map();
    incurredRows.forEach((row) => byCategoryMap.set(row.category, (byCategoryMap.get(row.category) || 0) + row.amountCents));
    const byCategory = Array.from(byCategoryMap.entries())
      .map(([category, cents]) => ({ category, label: CATEGORY_LABELS[category] || category || "Otros", amount: cents / 100 }))
      .sort((a, b) => b.amount - a.amount);

    return {
      incurredTotal: incurredCents / 100,
      paidTotal: paidCents / 100,
      pendingTotal: pendingCents / 100,
      previousIncurredTotal: previousIncurredCents / 100,
      previousPaidTotal: previousPaidCents / 100,
      periodCount: incurredRows.length,
      paidCount: paidRows.length,
      allCount: activeExpenses.length,
      voidedCount: voidedExpenses.length,
      hasPeriodExpenses: incurredRows.length > 0,
      byCategory,
    };
  }, [incurredRows, paidRows, previousIncurredRows, previousPaidRows, activeExpenses.length, voidedExpenses.length]);

  useEffect(() => {
    if (typeof onSummaryChange === "function") onSummaryChange(summary);
  }, [summary, onSummaryChange]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, scope, recordFilter, currentStart, currentEnd]);

  const beginCreate = () => {
    setForm(emptyForm());
    setFormOpen(true);
    setStatusMessage("");
  };

  const beginEdit = (row) => {
    setForm({
      id: row.id,
      dateKey: row.dateKey,
      amount: (row.amountCents / 100).toFixed(2),
      category: row.category,
      vendor: row.vendor,
      paymentMethod: row.paymentMethod,
      expenseType: row.expenseType,
      recurrence: row.recurrence,
      status: row.status,
      paidDateKey: row.paidDateKey || row.dateKey,
      notes: row.notes,
    });
    setFormOpen(true);
    setStatusMessage("");
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm(emptyForm());
  };

  const saveExpense = async (event) => {
    event?.preventDefault?.();
    if (!businessId || saving) return;
    const amountCents = centsFromInput(form.amount);
    if (!form.dateKey || !amountCents || !form.category) {
      setStatusMessage("Completa fecha, monto y categoría antes de guardar.");
      return;
    }

    const uid = String(auth?.currentUser?.uid || "").trim();
    if (!uid) {
      setStatusMessage("Necesitas una sesión autenticada para registrar gastos.");
      return;
    }

    const now = new Date().toISOString();
    const basePayload = {
      schemaVersion: EXPENSE_SCHEMA_VERSION,
      businessId: String(businessId),
      amountCents,
      currency: region.currency || "USD",
      dateKey: String(form.dateKey),
      category: String(form.category),
      vendor: String(form.vendor || "").trim().slice(0, 160),
      paymentMethod: String(form.paymentMethod || "other"),
      expenseType: String(form.expenseType || "variable") === "fixed" ? "fixed" : "variable",
      recurrence: ["none", "weekly", "monthly", "yearly"].includes(String(form.recurrence || "none")) ? String(form.recurrence || "none") : "none",
      status: String(form.status || "paid") === "pending" ? "pending" : "paid",
      paidDateKey: String(form.status || "paid") === "pending" ? "" : String(form.paidDateKey || form.dateKey),
      notes: String(form.notes || "").trim().slice(0, 1000),
      active: true,
      updatedAt: now,
      updatedByUid: uid,
      voidedAt: "",
      voidedByUid: "",
    };

    setSaving(true);
    setStatusMessage("");
    try {
      if (form.id) {
        await fsClient.setDocByPath(`${collectionPath}/${form.id}`, basePayload, { db, merge: true });
      } else {
        await fsClient.addDocToCollectionPath(collectionPath, {
          ...basePayload,
          createdAt: now,
          createdByUid: uid,
        }, { db });
      }
      await loadExpenses();
      setFormOpen(false);
      setForm(emptyForm());
      setStatusMessage(form.id ? "Gasto actualizado." : "Gasto registrado.");
    } catch (error) {
      console.error("[ExpenseLedger] save failed", error);
      setStatusMessage("No pudimos guardar el gasto. Revisa permisos y vuelve a intentar.");
    } finally {
      setSaving(false);
    }
  };

  const voidExpense = async (row) => {
    if (!row?.id || saving) return;
    const approved = typeof window === "undefined" ? true : window.confirm(`¿Anular ${MONEY.format(row.amount)} de ${CATEGORY_LABELS[row.category] || "gastos"}? El registro se conserva para auditoría.`);
    if (!approved) return;
    const uid = String(auth?.currentUser?.uid || "").trim();
    if (!uid) {
      setStatusMessage("Necesitas una sesión autenticada para anular gastos.");
      return;
    }
    const now = new Date().toISOString();
    setSaving(true);
    setStatusMessage("");
    try {
      await fsClient.setDocByPath(`${collectionPath}/${row.id}`, {
        active: false,
        updatedAt: now,
        updatedByUid: uid,
        voidedAt: now,
        voidedByUid: uid,
      }, { db, merge: true });
      await loadExpenses();
      setStatusMessage("Gasto anulado. El registro quedó conservado para auditoría.");
    } catch (error) {
      console.error("[ExpenseLedger] void failed", error);
      setStatusMessage("No pudimos anular el gasto.");
    } finally {
      setSaving(false);
    }
  };

  const sourceRows = useMemo(() => {
    if (recordFilter === "voided") return voidedExpenses;
    if (recordFilter === "all") return expenses;
    if (scope === "all") return activeExpenses;
    return incurredRows;
  }, [recordFilter, scope, expenses, activeExpenses, voidedExpenses, incurredRows]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return sourceRows
      .filter((row) => categoryFilter === "all" || row.category === categoryFilter)
      .filter((row) => {
        if (!q) return true;
        const haystack = [
          row.vendor,
          row.notes,
          CATEGORY_LABELS[row.category] || row.category,
          METHOD_LABELS[row.paymentMethod] || row.paymentMethod,
          row.expenseType,
          row.status,
        ].join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .slice()
      .sort((a, b) => String(b.dateKey || "").localeCompare(String(a.dateKey || "")) || String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
  }, [sourceRows, categoryFilter, search]);

  const pages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (!open) return null;

  return (
    <div className="loop-expense-ledger-layer" role="presentation" onClick={() => !saving && onClose?.()}>
      <aside className="loop-expense-ledger" role="dialog" aria-modal="true" aria-label={t("Gastos operativos")} onClick={(event) => event.stopPropagation()}>
        <header className="loop-expense-ledger-head">
          <div>
            <span>{t("FINANZAS")}</span>
            <h3>{t("Gastos operativos")}</h3>
            <p>{t("Registra gastos reales para completar la lectura del P&L y del flujo de caja.")}</p>
          </div>
          <div className="loop-expense-ledger-head-actions">
            <button type="button" className="primary" onClick={beginCreate}>{t("+ Nuevo gasto")}</button>
            <button type="button" className="close" onClick={() => !saving && onClose?.()} aria-label={t("Cerrar")}>×</button>
          </div>
        </header>

        <section className="loop-expense-ledger-summary" aria-label={`Resumen de gastos de ${periodLabel}`}>
          <article>
            <span>{t("Gastos del período")}</span>
            <strong>{MONEY.format(summary.incurredTotal)}</strong>
            <em>{summary.periodCount}{" " + t("registro")}{summary.periodCount === 1 ? "" : "s"} · {periodLabel}</em>
          </article>
          <article>
            <span>{t("Pagado en caja")}</span>
            <strong>{MONEY.format(summary.paidTotal)}</strong>
            <em>{summary.paidCount}{" " + t("pago")}{summary.paidCount === 1 ? "" : "s"}{" " + t("en el período")}</em>
          </article>
          <article className={summary.pendingTotal > 0 ? "warning" : ""}>
            <span>{t("Pendiente")}</span>
            <strong>{MONEY.format(summary.pendingTotal)}</strong>
            <em>{t("Gastos incurridos aún no pagados")}</em>
          </article>
        </section>

        {summary.byCategory.length ? (
          <section className="loop-expense-ledger-categories" aria-label={t("Gastos por categoría")}>
            {summary.byCategory.slice(0, 6).map((item) => (
              <div key={item.category}>
                <span>{t(item.label)}</span>
                <strong>{MONEY.format(item.amount)}</strong>
              </div>
            ))}
          </section>
        ) : null}

        <section className="loop-expense-ledger-tools">
          <div className="loop-expense-ledger-segments">
            <button type="button" className={scope === "period" ? "active" : ""} onClick={() => setScope("period")}>{t("Este período")}</button>
            <button type="button" className={scope === "all" ? "active" : ""} onClick={() => setScope("all")}>{t("Todo")}</button>
          </div>
          <select value={recordFilter} onChange={(event) => setRecordFilter(event.target.value)}>
            <option value="active">{t("Activos")}</option>
            <option value="voided">{t("Anulados")}</option>
            <option value="all">{t("Activos + anulados")}</option>
          </select>
          <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value="loop_fees">{t("Comisiones de Loop")}</option>
            <option value="all">{t("Todas las categorías")}</option>
            {EXPENSE_CATEGORIES.map((item) => <option key={item.key} value={item.key}>{t(item.label)}</option>)}
          </select>
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("Buscar proveedor, nota o categoría")} />
          <span>{filteredRows.length}{" " + t("registro")}{filteredRows.length === 1 ? "" : "s"}</span>
        </section>

        {statusMessage ? <div className="loop-expense-ledger-status">{statusMessage}</div> : null}

        <div className="loop-expense-ledger-list">
          {loading ? <div className="loop-expense-ledger-empty">{t("Cargando gastos…")}</div> : pageRows.length ? pageRows.map((row) => (
            <article className={`loop-expense-ledger-row ${row.active === false ? "is-voided" : ""}`} key={row.id}>
              <div className="main">
                <strong>{CATEGORY_LABELS[row.category] || t("Otros")}</strong>
                <span>{row.vendor || "Sin proveedor"}</span>
                <em>{formatDateKey(row.dateKey)} · {row.expenseType === "fixed" ? t("Fijo") : t("Variable")}{row.recurrence !== "none" ? ` · ${RECURRENCE_OPTIONS.find((item) => item.key === row.recurrence)?.label || row.recurrence}` : ""}</em>
                {row.notes ? <small>{row.notes}</small> : null}
              </div>
              <div className="money">
                <span>{row.status === "paid" ? t("Pagado") : t("Pendiente")}</span>
                <strong>{MONEY.format(row.amount)}</strong>
                <em>{METHOD_LABELS[row.paymentMethod] || row.paymentMethod || "Sin método"}{row.status === "paid" && row.paidDateKey ? ` · ${formatDateKey(row.paidDateKey)}` : ""}</em>
              </div>
              <div className="actions">
                {row.active !== false && row.sourceType !== "loop_commission" ? <button type="button" onClick={() => beginEdit(row)}>{t("Editar")}</button> : <span>{row.sourceType === "loop_commission" ? "Automático" : "Anulado"}</span>}
                {row.active !== false && row.sourceType !== "loop_commission" ? <button type="button" className="danger" onClick={() => voidExpense(row)}>{t("Anular")}</button> : null}
              </div>
            </article>
          )) : <div className="loop-expense-ledger-empty">{t("No hay gastos que coincidan con este filtro.")}</div>}
        </div>

        <footer className="loop-expense-ledger-foot">
          <button type="button" disabled={safePage <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>{t("Anterior")}</button>
          <span>{t("Página") + " "}{safePage} de {pages}</span>
          <button type="button" disabled={safePage >= pages} onClick={() => setPage((value) => Math.min(pages, value + 1))}>{t("Siguiente")}</button>
        </footer>

        {formOpen ? (
          <div className="loop-expense-form-layer" role="presentation" onClick={closeForm}>
            <form className="loop-expense-form" onSubmit={saveExpense} onClick={(event) => event.stopPropagation()}>
              <header>
                <div>
                  <span>{t("GASTO OPERATIVO")}</span>
                  <h4>{form.id ? "Editar gasto" : "Nuevo gasto"}</h4>
                  <p>{t("Los gastos de inventario ya asignados a servicios no deben duplicarse aquí.")}</p>
                </div>
                <button type="button" onClick={closeForm} aria-label={t("Cerrar")}>×</button>
              </header>

              <div className="loop-expense-form-grid">
                <label>
                  <span>{t("Fecha del gasto")}</span>
                  <input type="date" value={form.dateKey} onChange={(event) => setForm((prev) => ({ ...prev, dateKey: event.target.value, paidDateKey: prev.status === "paid" && !prev.id ? event.target.value : prev.paidDateKey }))} required />
                </label>
                <label>
                  <span>{t("Importe")} ({MONEY.resolvedOptions().currency})</span>
                  <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} placeholder="0.00" required />
                </label>
                <label>
                  <span>{t("Categoría")}</span>
                  <select value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}>
                    {EXPENSE_CATEGORIES.map((item) => <option key={item.key} value={item.key}>{t(item.label)}</option>)}
                  </select>
                </label>
                <label>
                  <span>{t("Proveedor")}</span>
                  <input type="text" maxLength={160} value={form.vendor} onChange={(event) => setForm((prev) => ({ ...prev, vendor: event.target.value }))} placeholder={t("Proveedor o beneficiario")} />
                </label>
                <label>
                  <span>{t("Tipo")}</span>
                  <select value={form.expenseType} onChange={(event) => setForm((prev) => ({ ...prev, expenseType: event.target.value }))}>
                    <option value="fixed">{t("Fijo")}</option>
                    <option value="variable">{t("Variable")}</option>
                  </select>
                </label>
                <label>
                  <span>{t("Recurrencia")}</span>
                  <select value={form.recurrence} onChange={(event) => setForm((prev) => ({ ...prev, recurrence: event.target.value }))}>
                    {RECURRENCE_OPTIONS.map((item) => <option key={item.key} value={item.key}>{t(item.label)}</option>)}
                  </select>
                  <small>{t("Solo referencia en esta versión; no genera cargos automáticamente.")}</small>
                </label>
                <label>
                  <span>{t("Estado")}</span>
                  <select value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value, paidDateKey: event.target.value === "paid" ? (prev.paidDateKey || prev.dateKey) : "" }))}>
                    <option value="paid">{t("Pagado")}</option>
                    <option value="pending">{t("Pendiente")}</option>
                  </select>
                </label>
                <label>
                  <span>{t("Método")}</span>
                  <select value={form.paymentMethod} onChange={(event) => setForm((prev) => ({ ...prev, paymentMethod: event.target.value }))}>
                    {PAYMENT_METHODS.map((item) => <option key={item.key} value={item.key}>{t(item.label)}</option>)}
                  </select>
                </label>
                {form.status === "paid" ? (
                  <label>
                    <span>{t("Fecha de pago")}</span>
                    <input type="date" value={form.paidDateKey} onChange={(event) => setForm((prev) => ({ ...prev, paidDateKey: event.target.value }))} required />
                  </label>
                ) : null}
                <label className="wide">
                  <span>{t("Notas")}</span>
                  <textarea maxLength={1000} rows={3} value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} placeholder={t("Detalle opcional")} />
                </label>
              </div>

              <footer>
                <button type="button" onClick={closeForm} disabled={saving}>{t("Cancelar")}</button>
                <button type="submit" className="primary" disabled={saving}>{saving ? "Guardando…" : form.id ? "Guardar cambios" : "Registrar gasto"}</button>
              </footer>
            </form>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
